// TagInput: a list of names as chips, each with a remove button, and a small field that adds one
// on Enter, a comma or leaving it (P-021: a machine's has and protect, merge flags, needs).
// `placeholder` greys the default when the list is empty.
import { useState } from 'react';
import { cn } from './cn';

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Names the field: "Add <label>", "Remove <tag> from <label>". */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
}

export function TagInput({ value, onChange, label, placeholder = '+ add', disabled, invalid, className }: TagInputProps) {
  const [draft, setDraft] = useState('');
  function commit(text: string) {
    const names = text.split(',').map((s) => s.trim()).filter(Boolean);
    const next = [...value, ...names.filter((n, i) => !value.includes(n) && names.indexOf(n) === i)];
    if (next.length !== value.length) onChange(next);
    setDraft('');
  }
  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-1', className)}>
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 rounded border border-hairline-strong bg-surface py-0.5 pl-2 pr-1 font-mono text-[11.5px] text-ink-display">
          {tag}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${tag} from ${label}`}
              className="cursor-pointer rounded px-0.5 text-ink-muted hover:text-ink-display"
              onClick={() => onChange(value.filter((t) => t !== tag))}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          aria-label={`Add ${label}`}
          aria-invalid={invalid || undefined}
          value={draft}
          placeholder={value.length ? '+ add' : placeholder}
          className={cn(
            'h-[26px] w-[110px] min-w-0 rounded border border-dashed bg-transparent px-2 font-mono text-[12px] text-ink-display placeholder:text-ink-muted',
            'focus:border-solid focus:outline-2 focus:-outline-offset-1 focus:outline-focus-ring coarse:min-h-10 coarse:text-[16px]',
            invalid ? 'border-danger' : 'border-hairline-strong',
          )}
          onChange={(e) => {
            const text = e.target.value;
            if (text.includes(',')) commit(text);
            else setDraft(text);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => commit(draft)}
        />
      )}
      {disabled && !value.length && <span className="text-[12px] text-ink-muted">{placeholder}</span>}
    </div>
  );
}
